import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOnboarding } from '../context/OnboardingContext';
import logoGala from '../assets/common/LogoGala.png';
import FormInput from '../components/FormInput';
import { apiCheckEmail } from '../services/api';
import { PASSWORD_RULES_ATTR, PWD_RULES, validatePassword } from '../utils/password';
import { validateEmail } from '../utils/signupValidation';
import './auth.css';

const SignUp = () => {
  const navigate = useNavigate();
  const { formData, updateField } = useOnboarding();
  const [errors, setErrors] = useState({});

  const pwd = formData.password || '';
  const confirmPwd = formData.confirmPassword || '';
  const pwdChecks = validatePassword(pwd);
  const allPwdValid = Object.values(pwdChecks).every(Boolean);
  const confirmMismatch = confirmPwd.length > 0 && confirmPwd !== pwd;

  const handleEmailBlur = useCallback(async () => {
    const email = (formData.email || '').trim();
    if (!email) return;
    const formatError = validateEmail(email);
    if (formatError) {
      setErrors((err) => ({ ...err, email: formatError }));
      return;
    }
    try {
      const { available } = await apiCheckEmail(email);
      if (!available) {
        setErrors((err) => ({ ...err, email: 'Cet email est déjà utilisé.' }));
      }
    } catch {
      // network error — silent, will be caught at submit
    }
  }, [formData.email]);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    const newErrors = {};

    const email = (formData.email || '').trim();
    const emailError = validateEmail(email);
    if (emailError) {
      newErrors.email = emailError;
    } else {
      // On mémorise l'adresse nettoyée : c'est elle qui partira à l'inscription.
      if (email !== formData.email) updateField('email', email);
      try {
        const { available } = await apiCheckEmail(email);
        if (!available) newErrors.email = 'Cet email est déjà utilisé.';
      } catch {
        // network error — continue, backend will reject if needed
      }
    }

    if (!allPwdValid) {
      newErrors.password = true;
    }

    if (confirmPwd !== pwd) {
      newErrors.confirmPassword = 'Les mots de passe ne correspondent pas.';
    }

    if (!formData.consentAccepted) {
      newErrors.consent = 'Vous devez accepter les conditions générales et la politique de confidentialité pour continuer.';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setErrors({});
    navigate('/onboarding/prenom');
  };

  return (
    <div className="page">
      <div className="container">
        <div className="signup-logo">
          <img src={logoGala} alt="CycleGala" className="signup-logo-image" />
        </div>

        <h2 className="signup-brand">
          <span className="signup-brand--cycle">Cycle</span>
          <span className="signup-brand--gala">Gala</span>
        </h2>
        <p className="subtitle">Créez votre compte</p>

        {/* Un vrai <form> avec `autocomplete` : c'est ce qui permet au trousseau
            iCloud / à Google / à 1Password de proposer un mot de passe fort puis de
            l'enregistrer avec l'adresse comme identifiant. */}
        <form onSubmit={handleSubmit} noValidate>
        <FormInput
          label="Email"
          type="email"
          name="email"
          autoComplete="username"
          inputMode="email"
          autoCapitalize="none"
          spellCheck={false}
          placeholder="votre.email@exemple.com"
          value={formData.email}
          error={errors.email}
          onChange={(e) => { updateField('email', e.target.value); setErrors((err) => ({ ...err, email: undefined })); }}
          onBlur={handleEmailBlur}
        />
        {errors.email && <p className="signup-error">{errors.email}</p>}

        <FormInput
          label="Mot de passe"
          type="password"
          name="new-password"
          autoComplete="new-password"
          passwordrules={PASSWORD_RULES_ATTR}
          placeholder="••••••••"
          value={formData.password}
          error={errors.password}
          onChange={(e) => { updateField('password', e.target.value); setErrors((err) => ({ ...err, password: undefined })); }}
        />
        {pwd.length > 0 && (
          <ul className="pwd-checklist">
            {PWD_RULES.map(({ key, label }) => (
              <li key={key} className={`pwd-checklist__item${pwdChecks[key] ? ' pwd-checklist__item--ok' : ''}`}>
                {pwdChecks[key] ? '✓' : '·'} {label}
              </li>
            ))}
          </ul>
        )}

        {errors.password && !pwd && <p className="signup-error">Le mot de passe est requis.</p>}

        <FormInput
          label="Confirmer le mot de passe"
          type="password"
          name="confirm-password"
          autoComplete="new-password"
          placeholder="••••••••"
          value={formData.confirmPassword}
          onChange={(e) => { updateField('confirmPassword', e.target.value); setErrors((err) => ({ ...err, confirmPassword: undefined })); }}
        />
        {(confirmMismatch || errors.confirmPassword) && (
          <p className="signup-error">Les mots de passe ne correspondent pas.</p>
        )}

        <label className="consent-checkbox">
          <input
            type="checkbox"
            id="consent-cgu"
            name="consent_cgu"
            checked={formData.consentAccepted}
            onChange={(e) => {
              updateField('consentAccepted', e.target.checked);
              setErrors((err) => ({ ...err, consent: undefined }));
            }}
          />
          <span>
            J'accepte les{' '}
            <button type="button" className="auth-link-btn" onClick={() => navigate('/legal/conditions')}>
              conditions générales
            </button>{' '}
            et la{' '}
            <button type="button" className="auth-link-btn" onClick={() => navigate('/legal/confidentialite')}>
              politique de confidentialité
            </button>
            , ainsi que le traitement de mes données de santé (RGPD Art. 7)
          </span>
        </label>
        {errors.consent && <p className="signup-error">{errors.consent}</p>}

        <div className="button-group">
          <button type="submit" className="btn btn--primary">
            Créer mon compte
          </button>
        </div>
        </form>

        <div className="auth-link-row">
          <span>Déjà un compte ? </span>
          <button type="button" className="auth-link-btn" onClick={() => navigate('/connexion')}>Se connecter</button>
        </div>

        <div className="auth-link-row">
          <span>Je suis coach ? </span>
          <button type="button" className="auth-link-btn" onClick={() => navigate('/inscription/coach')}>
            Faire une demande
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignUp;
